import React from 'react';
import {
    PanelHeader,
    PanelHeaderButton,
    Div,
    SplitCol,
    SplitLayout,
    Button, ButtonGroup, Chip, Title,
    ModalRoot, ModalPage, ModalPageHeader
} from '@vkontakte/vkui';

import {
    Icon20CameraCircleFillGreen,
    Icon24Cancel,
    Icon24QuestionOutline
} from "@vkontakte/icons";

import {AdaptivityContext, getImageAngles, setQuizStep} from "../assets/constants";
import "./style.css"

import BaseComponent from "../components/BaseComponent";
import withOffline from "../components/withOffline";
import SalePointMonitoringDocsApi from "../api/SalePointMonitoringDocs";
import HelpButton from "../assets/HelpButton";
import PhotoGallery from "../components/PhotoGallery";
import TakeAPhotoToQueue from "../components/TakeAPhotoToQueue";
import PhotoWrapper from "../utils/PhotoWrapper";
import PDFViewer from "../assets/PDFViewer";

const MONITORING_DOC_TYPE = 'sale_point_monitoring';
const MODAL_ANGLE_HELP = 'modal_angle_help';
const MODAL_ANGLE_INSTRUCTION = 'modal_angle_instruction';


class SalePointMonitoringStep2 extends BaseComponent {

    // Photo angles for monitoring documents from the cached reference
    getMonitoringAngles = () => {
        const anglesRaw = getImageAngles() || {};
        return Object.values(anglesRaw).filter(a =>
            a && a.doc_type === MONITORING_DOC_TYPE && (a.media_type ?? 'photo') === 'photo'
        );
    }

    getImagesForAngle = (images, angleId) => {
        const result = {};
        Object.keys(images || {}).forEach(key => {
            const item = images[key];
            if (item && Number(item.angle_id ?? 0) === Number(angleId)) {
                result[key] = item;
            }
        });
        return result;
    }

    countAngleImages = (images, angleId) => {
        return Object.values(images || {}).filter(i =>
            i && i.hasImage && Number(i.angle_id ?? 0) === Number(angleId)
        ).length;
    }

    getFirstAngleCoverUrl = (images, angle, imagesUrl) => {
        const first = Object.values(images || {}).find(i =>
            i && i.hasImage && Number(i.angle_id ?? 0) === Number(angle.id)
        );
        if (first) {
            const path = (first.thumb || first.mainImage || '')
                .replace('https:/l', 'https://l')
                .replace('https://lab.scladro.ru', '')
                .replace(imagesUrl, '');
            return imagesUrl + path;
        }
        // Examples stay in the help modal. Using them as tile covers looks like
        // leftover photos from another document.
        return null;
    }

    toggleAngleExpanded = (angleId) => {
        const current = this.state.expandedAngleId;
        this.setState({expandedAngleId: Number(current) === Number(angleId) ? null : Number(angleId)});
    }

    handleMonitoringImagesChange = (updatedImages) => {
        const {current_document} = this.props;
        if (!current_document || !current_document.doc_data) return;

        current_document.doc_data.images = updatedImages;
        window.postMessage({type: 'setCurrentSaleDocMonitoring', value: current_document});

        // Сохраняем на сервер только если документ имеет ID
        if (current_document.doc_data.id && current_document.doc_data.id > 0) {
            SalePointMonitoringDocsApi.setImages({doc_id: current_document.doc_data.id, images: JSON.stringify(updatedImages)});
        }
        setTimeout(() => this.setState({}), 100);
    }

    // Merges one angle's gallery subset back into the full images map.
    // Keys produced inside a subset gallery can collide with keys of other
    // angles' images - such entries get the next free numeric key instead.
    handleAngleImagesChange = (angleId) => (updatedSubset) => {
        const {current_document} = this.props;
        if (!current_document || !current_document.doc_data) return;

        const all = {...(current_document.doc_data.images ?? {})};

        Object.keys(all).forEach(key => {
            const item = all[key];
            if (item && Number(item.angle_id ?? 0) === Number(angleId)) {
                delete all[key];
            }
        });

        let nextFreeKey = 1 + Object.keys(all).reduce((max, k) => Math.max(max, Number(k) || 0), 0);

        Object.keys(updatedSubset || {}).forEach(key => {
            const item = updatedSubset[key];
            if (!item) return;
            const targetKey = all[key] === undefined ? key : nextFreeKey++;
            // Prefer angle_id already set on the item (e.g. from capture picker or change-angle)
            const itemAngle = item.angle_id !== undefined && item.angle_id !== null
                ? Number(item.angle_id)
                : Number(angleId);
            all[targetKey] = {...item, angle_id: itemAngle};
        });

        this.handleMonitoringImagesChange(all);
    }

    handleImageRotated = (key, updatedImage, updatedImages) => {
        setTimeout(() => this.setState({}), 100);
    }

    handleImageAdded = (key, newImage, updatedImages) => {
        setTimeout(() => this.setState({}), 100);
    }

    receiveMessage = (event) => {
        if (event.data.type != undefined && event.data.type == 'reloadSalePointMonitoringImages') {
            const {current_document} = this.props;
            if (!current_document || !current_document.doc_data) return;

            let items = current_document.doc_data.images ? current_document.doc_data.images : {};

            if (current_document.doc_data.id && current_document.doc_data.id > 0) {
                SalePointMonitoringDocsApi.setImages({doc_id: current_document.doc_data.id, images: JSON.stringify(items)});
            }
        }
        if (event.data == 'SalePointMonitoringGoGetBack') {
            setQuizStep(1, 'SalePointMonitoring');
        }
    }

    nextStep = () => {
        const {current_document} = this.props;
        const images = current_document.doc_data && current_document.doc_data.images ? current_document.doc_data.images : {};

        let errors = 0;
        this.getMonitoringAngles().forEach(angle => {
            if (Number(angle.required) !== 1) return;
            const need = Math.max(1, Number(angle.min_count) || 0);
            const cnt = this.countAngleImages(images, angle.id);
            if (cnt < need) {
                errors++;
                window.postMessage({
                    type: 'error',
                    text: `Ракурс «${angle.title}»: нужно минимум ${need} фото (сделано ${cnt})`
                });
            }
        });

        if (errors === 0) {
            setQuizStep(3, 'SalePointMonitoring');
        }
    }

    openAngleInstruction = (angle) => {
        this.setState({currentInstructionAngle: angle});
        this.setActiveModal(MODAL_ANGLE_INSTRUCTION);
    }

    openAngleHelp = (angle) => {
        this.setState({currentInstructionAngle: angle});
        this.setActiveModal(MODAL_ANGLE_HELP);
    }

    hasAngleHelp = (angle) => {
        const hasExamples = Array.isArray(angle.examples) && angle.examples.length > 0;
        return hasExamples || !!angle.instruction_pdf;
    }

    renderAngleTile = (angle, images, imagesUrl) => {
        const need = Number(angle.min_count) || 0;
        const cnt = this.countAngleImages(images, angle.id);
        const isRequired = Number(angle.required) === 1;
        const complete = isRequired && cnt >= Math.max(1, need);
        const incomplete = isRequired && !complete;
        const watermark = isRequired && need > 0 ? `${cnt} из ${need}` : String(cnt);
        const cover = this.getFirstAngleCoverUrl(images, angle, imagesUrl);
        const isActive = Number(this.state.expandedAngleId) === Number(angle.id);

        let titleClass = 'angle-tile__title';
        if (incomplete) titleClass += ' angle-tile__title--need';
        else if (complete) titleClass += ' angle-tile__title--ok';
        else titleClass += ' angle-tile__title--neutral';

        return (
            <div
                key={`angle-tile-${angle.id}`}
                className={`angle-tile${isActive ? ' angle-tile--active' : ''}`}
                onClick={() => this.toggleAngleExpanded(angle.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.toggleAngleExpanded(angle.id);
                    }
                }}
            >
                <div className={titleClass}>{angle.title}</div>
                <div className="angle-tile__cover">
                    {cover
                        ? <PhotoWrapper src={cover} fill alt={angle.title}/>
                        : <div className="angle-tile__cover-empty" aria-hidden="true"/>}
                    <div className="angle-tile__watermark">{watermark}</div>
                </div>
            </div>
        );
    }

    renderExpandedAngle = (angle, images, base_url) => {
        if (!angle) return null;
        const angles = this.getMonitoringAngles();

        return (
            <div className="angle-expanded">
                <div className="angle-expanded__toolbar">
                    {this.hasAngleHelp(angle) &&
                        <Button mode="tertiary" size="s"
                                before={<Icon24QuestionOutline width={22} height={22}/>}
                                onClick={() => this.openAngleHelp(angle)}
                                aria-label="Как снимать"/>}
                    <div className="angle-expanded__title">{angle.title}</div>
                </div>
                <PhotoGallery
                    images={this.getImagesForAngle(images, angle.id)}
                    title=""
                    base_url={base_url}
                    destination="brands"
                    eventName={`setSalePointMonitoringImage_angle_${angle.id}`}
                    usePhotoWrapper={true}
                    fancyboxGroup={`galleryAngle${angle.id}`}
                    onImagesChange={this.handleAngleImagesChange(angle.id)}
                    onImageRotated={this.handleImageRotated}
                    onImageAdded={this.handleImageAdded}
                    PhotoComponent={TakeAPhotoToQueue}
                    angles={angles}
                    angleId={angle.id}
                />
            </div>
        );
    }

    openGreenCamera = () => {
        const ref = this.photoGalleryRef || this.noAngleGalleryRef;
        if (ref) {
            const nextKey = ref.getNextKey();
            ref.getPhoto(nextKey);
        }
    }

    render() {
        const {current_document, base_url} = this.props;

        const items_monitoring = current_document.doc_data && current_document.doc_data.images ? current_document.doc_data.images : {};
        const angles = this.getMonitoringAngles();
        const images_url = base_url.replace("frontend/", '');
        const helpAngle = this.state.currentInstructionAngle;
        const expandedAngle = angles.find(a => Number(a.id) === Number(this.state.expandedAngleId));
        const noAngleImages = this.getImagesForAngle(items_monitoring, 0);
        const noAngleCount = this.countAngleImages(items_monitoring, 0);

        const modals = (
            <ModalRoot activeModal={this.state.activeModal}>
                <ModalPage settlingHeight={100} id={MODAL_ANGLE_HELP} onClose={this.modalBack}
                           header={
                               <ModalPageHeader
                                   after={<PanelHeaderButton aria-label="Закрыть"
                                                             onClick={this.modalBack}><Icon24Cancel/></PanelHeaderButton>}>
                                   Как снимать{helpAngle ? `: ${helpAngle.title}` : ''}
                               </ModalPageHeader>
                           }>
                    {helpAngle &&
                        <Div>
                            {Array.isArray(helpAngle.examples) && helpAngle.examples.length > 0 &&
                                <>
                                    <Title level="3" style={{marginBottom: 8}}>Примеры</Title>
                                    <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16}}>
                                        {helpAngle.examples.map((example, i) => (
                                            <a key={`help-ex-${helpAngle.id}-${i}`} href={images_url + example}
                                               target="_blank" rel="noreferrer">
                                                <img src={images_url + example} alt="Пример"
                                                     style={{height: 120, borderRadius: 6}}/>
                                            </a>
                                        ))}
                                    </div>
                                </>}
                            {helpAngle.instruction_pdf &&
                                <Button mode="primary" size="m"
                                        onClick={() => this.openAngleInstruction(helpAngle)}>
                                    Открыть PDF-инструкцию
                                </Button>}
                        </Div>}
                </ModalPage>
                <ModalPage settlingHeight={100} id={MODAL_ANGLE_INSTRUCTION} onClose={this.modalBack}
                           header={
                               <ModalPageHeader
                                   after={<PanelHeaderButton aria-label="Закрыть"
                                                             onClick={this.modalBack}><Icon24Cancel/></PanelHeaderButton>}>
                                   PDF{helpAngle ? `: ${helpAngle.title}` : ''}
                               </ModalPageHeader>
                           }>
                    {helpAngle && helpAngle.instruction_pdf &&
                        <PDFViewer file={images_url + helpAngle.instruction_pdf}/>}
                </ModalPage>
            </ModalRoot>
        );

        return (
            <SplitLayout modal={modals}>
                <SplitCol>
                    <PanelHeader fixed={true}>
                        <Div>
                            Мониторинг точки. Шаг 2
                        </Div>
                    </PanelHeader>

                    <SplitLayout>
                        <SplitCol>
                            {this.props.isOffline &&
                                <Chip style={{float: 'right'}} removable={false} className="all-docs"
                                      id="is_offline">Offline режим<HelpButton
                                    text={"Нет связи с интернет. Работаем в автономном режиме"}/></Chip>}
                            <PhotoGallery
                                images={current_document.doc_data.sale_point.images}
                                title="Фотографии торговой точки"
                                base_url={base_url}
                                fancyboxGroup="galleryIncoming3"
                                emptyMessage="Нет фотографий точки"
                                readOnly={true}
                                galleryStyle={{display: 'flex', flexWrap: "wrap", maxHeight: '600px', overflowY: 'auto'}}
                            />
                        </SplitCol>
                    </SplitLayout>

                    {angles.length > 0 &&
                        <>
                            <div className="angle-tiles">
                                {angles.map(angle => this.renderAngleTile(angle, items_monitoring, images_url))}
                            </div>
                            {this.renderExpandedAngle(expandedAngle, items_monitoring, base_url)}

                            {/* Always mounted so green camera can open capture with angle select */}
                            <div style={noAngleCount > 0 ? undefined : {position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden'}}>
                                <PhotoGallery
                                    ref={ref => this.noAngleGalleryRef = ref}
                                    images={noAngleImages}
                                    title={noAngleCount > 0 ? "Без ракурса" : ""}
                                    base_url={base_url}
                                    destination="brands"
                                    eventName="setSalePointMonitoringImage"
                                    usePhotoWrapper={true}
                                    fancyboxGroup="galleryNoAngle"
                                    onImagesChange={this.handleAngleImagesChange(0)}
                                    onImageRotated={this.handleImageRotated}
                                    onImageAdded={this.handleImageAdded}
                                    PhotoComponent={TakeAPhotoToQueue}
                                    angles={angles}
                                    showAddButton={false}
                                />
                            </div>
                        </>}

                    {angles.length === 0 &&
                        <SplitLayout>
                            <SplitCol>
                                <PhotoGallery
                                    ref={ref => this.photoGalleryRef = ref}
                                    images={this.getImagesForAngle(items_monitoring, 0)}
                                    title="Произвольные фото"
                                    base_url={base_url}
                                    destination="brands"
                                    eventName="setSalePointMonitoringImage"
                                    usePhotoWrapper={true}
                                    fancyboxGroup="galleryIncoming2"
                                    onImagesChange={this.handleAngleImagesChange(0)}
                                    onImageRotated={this.handleImageRotated}
                                    onImageAdded={this.handleImageAdded}
                                    PhotoComponent={TakeAPhotoToQueue}
                                />
                            </SplitCol>
                        </SplitLayout>}

                    <SplitLayout>
                        <SplitCol>
                            <Div className="action-board">
                                <ButtonGroup mode="horizontal" stretched>
                                    <Button stretched size={AdaptivityContext.isMobile ? 'm' : 'l'}
                                            onClick={() => setQuizStep(1, 'SalePointMonitoring')}>Назад</Button>
                                    <Button mode="outline" size={AdaptivityContext.isMobile ? 'm' : 'l'}
                                            onClick={this.openGreenCamera}><Icon20CameraCircleFillGreen/></Button>
                                    <Button stretched size={AdaptivityContext.isMobile ? 'm' : 'l'}
                                            onClick={() => this.nextStep()}>Далее</Button>
                                </ButtonGroup>
                            </Div>
                        </SplitCol>
                    </SplitLayout>
                </SplitCol>
            </SplitLayout>);
    }
}

export default withOffline(SalePointMonitoringStep2)
